/**
 * Client-side distance helpers for Obchody directory (haversine + buckets).
 * No map UI — sort/group only.
 */

export type DirectoryDistanceBucketId =
  | 'nearby'
  | 'nearbyArea'
  | 'further'
  | 'noCoordinates';

/** Nearby bucket: ≤ 2 km. */
export const DIRECTORY_NEARBY_MAX_METERS = 2000;
/** Nearby-area bucket: ≤ 10 km (and > nearby). */
export const DIRECTORY_AREA_MAX_METERS = 10_000;

const BUCKET_ORDER: readonly DirectoryDistanceBucketId[] = [
  'nearby',
  'nearbyArea',
  'further',
  'noCoordinates',
];

export function haversineMeters(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const earthRadiusM = 6_371_000;
  const toRad = (deg: number): number => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadiusM * Math.asin(Math.min(1, Math.sqrt(a)));
}

/** Unit templates for {@link formatDistanceMeters}; `{{value}}` is replaced. */
export interface FormatDistanceMetersLabels {
  readonly meters: string;
  readonly kilometers: string;
}

const DEFAULT_DISTANCE_LABELS: FormatDistanceMetersLabels = {
  meters: '{{value}} m',
  kilometers: '{{value}} km',
};

function applyDistanceTemplate(template: string, value: string): string {
  return template.split('{{value}}').join(value);
}

/**
 * Formats meters as "500 m" or "1.2 km" (≥1000 → one decimal km).
 * Pass i18n templates (e.g. `t('directory.distance.meters')`) for localized units.
 */
export function formatDistanceMeters(
  meters: number,
  labels: FormatDistanceMetersLabels = DEFAULT_DISTANCE_LABELS,
): string {
  const safe = Number.isFinite(meters) ? Math.max(0, meters) : 0;
  if (safe < 1000) {
    return applyDistanceTemplate(labels.meters, String(Math.round(safe)));
  }
  return applyDistanceTemplate(labels.kilometers, (safe / 1000).toFixed(1));
}

export function classifyDirectoryDistanceBucket(
  distanceMeters: number | null,
): DirectoryDistanceBucketId {
  if (distanceMeters === null || !Number.isFinite(distanceMeters)) {
    return 'noCoordinates';
  }
  if (distanceMeters <= DIRECTORY_NEARBY_MAX_METERS) {
    return 'nearby';
  }
  if (distanceMeters <= DIRECTORY_AREA_MAX_METERS) {
    return 'nearbyArea';
  }
  return 'further';
}

/** Presence-aware shop shape — ONLINE_ONLY is never distance-eligible (A11c). */
export type DirectoryDistanceShopGeo = {
  readonly lat?: number | null;
  readonly lng?: number | null;
  /** When `ONLINE_ONLY`, treat as noCoordinates even if leftover coords leak. */
  readonly customerPresence?: 'STOREFRONT' | 'ONLINE_ONLY' | string | null;
};

export function shopDistanceMeters(
  shop: DirectoryDistanceShopGeo,
  userLat: number,
  userLng: number,
): number | null {
  // A11c — ONLINE_ONLY is list-only (noCoordinates); never proximity meters.
  if (shop.customerPresence === 'ONLINE_ONLY') {
    return null;
  }
  const lat = shop.lat;
  const lng = shop.lng;
  if (
    typeof lat !== 'number' ||
    typeof lng !== 'number' ||
    !Number.isFinite(lat) ||
    !Number.isFinite(lng)
  ) {
    return null;
  }
  return haversineMeters(userLat, userLng, lat, lng);
}

export interface DirectoryDistanceSection<T> {
  readonly bucket: DirectoryDistanceBucketId;
  readonly items: T[];
}

/**
 * Group shops into distance buckets (nearby → area → further → no coords),
 * sorting each bucket by ascending distance (name tie-break for unknowns).
 */
export function groupDirectoryShopsByDistance<
  T extends {
    readonly name: string;
    readonly lat?: number | null;
    readonly lng?: number | null;
    readonly customerPresence?: 'STOREFRONT' | 'ONLINE_ONLY' | string | null;
  },
>(
  items: readonly T[],
  userLat: number,
  userLng: number,
): DirectoryDistanceSection<T>[] {
  const buckets: Record<DirectoryDistanceBucketId, T[]> = {
    nearby: [],
    nearbyArea: [],
    further: [],
    noCoordinates: [],
  };
  const distanceByItem = new Map<T, number | null>();

  for (const item of items) {
    // A11c — ONLINE_ONLY → noCoordinates only (never nearby / area / further).
    if (item.customerPresence === 'ONLINE_ONLY') {
      distanceByItem.set(item, null);
      buckets.noCoordinates.push(item);
      continue;
    }
    const distance = shopDistanceMeters(item, userLat, userLng);
    distanceByItem.set(item, distance);
    buckets[classifyDirectoryDistanceBucket(distance)].push(item);
  }

  const compareLocale = (a: string, b: string): number =>
    a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });

  for (const bucketId of BUCKET_ORDER) {
    buckets[bucketId].sort((left, right) => {
      const dLeft = distanceByItem.get(left) ?? null;
      const dRight = distanceByItem.get(right) ?? null;
      if (dLeft !== null && dRight !== null && dLeft !== dRight) {
        return dLeft - dRight;
      }
      if (dLeft !== null && dRight === null) {
        return -1;
      }
      if (dLeft === null && dRight !== null) {
        return 1;
      }
      return compareLocale(left.name, right.name);
    });
  }

  return BUCKET_ORDER.filter((bucket) => buckets[bucket].length > 0).map((bucket) => ({
    bucket,
    items: buckets[bucket],
  }));
}
